import { useEffect, useLayoutEffect, useRef } from 'react'

interface NumberWheelProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  label?: string
}

const ITEM_SIZE = 56

export function NumberWheel({ value, onChange, min = 0, max = 40, label = 'Фактически выполнено' }: NumberWheelProps) {
  const viewport = useRef<HTMLDivElement>(null)
  const frame = useRef<number | undefined>(undefined)
  const lastEmitted = useRef<number | undefined>(undefined)
  const values = Array.from({ length: max - min + 1 }, (_, index) => min + index)

  const scrollToValue = (next: number, behavior: ScrollBehavior = 'smooth') => {
    viewport.current?.scrollTo({ top: (next - min) * ITEM_SIZE, behavior })
  }

  useLayoutEffect(() => scrollToValue(value, 'auto'), []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const element = viewport.current
    if (lastEmitted.current === value) return
    if (!element || Math.abs(element.scrollTop - (value - min) * ITEM_SIZE) < 2) return
    scrollToValue(value)
  }, [min, value])
  useEffect(() => () => { if (frame.current) cancelAnimationFrame(frame.current) }, [])

  const onScroll = () => {
    if (frame.current) cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => {
      const next = Math.max(min, Math.min(max, Math.round((viewport.current?.scrollTop || 0) / ITEM_SIZE) + min))
      if (next !== value) { lastEmitted.current = next; onChange(next) }
    })
  }

  const changeBy = (delta: number) => {
    const next = Math.max(min, Math.min(max, value + delta))
    lastEmitted.current = next
    onChange(next)
    scrollToValue(next)
  }

  return (
    <div className="number-wheel-field">
      <div className="number-wheel-field__label"><span>{label}</span><strong aria-live="polite">{value}</strong></div>
      <div className="number-wheel__viewport">
        <div
          ref={viewport}
          className="number-wheel"
          role="spinbutton"
          tabIndex={0}
          aria-label={label}
          aria-orientation="vertical"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          onScroll={onScroll}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); changeBy(-1) }
            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); changeBy(1) }
            if (event.key === 'Home') { event.preventDefault(); lastEmitted.current = min; onChange(min); scrollToValue(min) }
            if (event.key === 'End') { event.preventDefault(); lastEmitted.current = max; onChange(max); scrollToValue(max) }
          }}
        >
          {values.map((item) => (
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={item === value ? 'number-wheel__item is-selected' : 'number-wheel__item'}
              key={item}
              onClick={() => { lastEmitted.current = item; onChange(item); scrollToValue(item) }}
            >{item}</button>
          ))}
        </div>
        <div className="number-wheel__focus" aria-hidden="true" />
      </div>
      <small>Смахни вверх или вниз</small>
    </div>
  )
}
